import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import SessionDisplaced from '../components/SessionDisplaced';

const SocketContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : 'http://localhost:3002';

// Anti-ratholing window. Must match DEPARTURE_WINDOW_MS in
// backend/socket/index.js — the server enforces it, this only explains it.
export const DEPARTURE_WINDOW_MS = 3 * 60 * 60 * 1000;

/* A stable id for this browser, sent on the socket handshake.
 *
 * The server allows one device per account and displaces the others, so it
 * needs to tell "the same browser reconnecting" — a reload, or socket.io
 * retrying after a blip — from "a second device signing in". A socket id
 * cannot: both look like a new connection. This can, because it outlives the
 * connection and is unique per browser profile.
 *
 * If storage is unavailable (private window, blocked site data) we fall back
 * to a per-tab id. The cost is that a reload then looks like a new device and
 * displaces itself, which is survivable — it reconnects either way.
 */
const CLIENT_ID_KEY = 'triadha.clientId';

function getClientId() {
  try {
    let id = window.localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() ?? `c${Date.now()}${Math.random()}`);
      window.localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return `ephemeral-${Math.random().toString(36).slice(2)}`;
  }
}

// ── Position derivation (pure utility, moved here from TableContext) ───────────
export function derivePositions(seats, dealerSeat, straddlers = []) {
  const n        = seats.length;
  const occupied = seats.map((s, i) => s.player ? i : null).filter(i => i !== null);
  if (occupied.length < 2) return {};
  const sorted = [...occupied].sort(
    (a, b) => ((a - dealerSeat + n) % n) - ((b - dealerSeat + n) % n)
  );
  const pos = {};
  sorted.forEach((idx, i) => {
    if      (i === 0) pos[idx] = 'D';
    else if (i === 1) pos[idx] = 'SB';
    else if (i === 2) pos[idx] = 'BB';
    else {
      const si = straddlers.indexOf(idx);
      if      (si === 0) pos[idx] = 'STR';
      else if (si === 1) pos[idx] = 'STR 2';
      else if (si === 2) pos[idx] = 'STR 3';
    }
  });
  return pos;
}

export function SocketProvider({ children }) {
  const { user, token, setWalletFromServer } = useAuth();

  const [socket,       setSocket]       = useState(null);
  const [connected,    setConnected]    = useState(false);
  const [tableMap,     setTableMap]     = useState({});  // tableId -> full state
  const [waitlists,    setWaitlists]    = useState({});  // tableId -> position
  const [departedPlayers] = useState({});
  const [myCardsMap,   setMyCardsMap]   = useState({});  // tableId -> { seatIndex, hands, validActions }
  const [handHistory,  setHandHistory]  = useState({});  // tableId -> [...hands]
  const [displaced,    setDisplaced]    = useState(false);

  const socketRef = useRef(null);

  // Connect / disconnect with auth token
  useEffect(() => {
    if (!token) {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; setSocket(null); setConnected(false); }
      return;
    }

    const s = io(BACKEND_URL, {
      auth: { token, name: user?.name, clientId: getClientId() },
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });
    socketRef.current = s;
    setSocket(s);

    s.on('connect',    () => { setConnected(true); setDisplaced(false); });
    s.on('disconnect', () => setConnected(false));

    // The account signed in on another device. Disconnect deliberately rather
    // than letting socket.io retry: reconnecting would displace that device in
    // turn and the two would trade the account back and forth forever.
    s.on('session:displaced', () => {
      setDisplaced(true);
      s.disconnect();
    });

    // Lobby: all tables
    s.on('tables:list', (tables) => {
      setTableMap(prev => {
        const next = { ...prev };
        for (const t of tables) next[t.id] = t;
        return next;
      });
    });

    // Individual table state update
    s.on('table:state', (state) => {
      setTableMap(prev => ({ ...prev, [state.id]: state }));
      // Sync hand history from gameState handCount changes
    });

    // Server-created table (admin)
    s.on('table:created', (state) => {
      setTableMap(prev => ({ ...prev, [state.id]: state }));
    });

    // Table deleted (admin)
    s.on('table:deleted', ({ tableId }) => {
      setTableMap(prev => { const n = { ...prev }; delete n[tableId]; return n; });
    });

    // Private hole cards + validActions
    s.on('player:cards', ({ tableId, seatIndex, hands, validActions }) => {
      setMyCardsMap(prev => ({ ...prev, [tableId]: { seatIndex, hands, validActions } }));
    });

    // Hand history — single new entry pushed after each hand
    s.on('hand:history_entry', ({ tableId, hand }) => {
      const humanSeat = user?.id
        ? (hand.seats?.findIndex(s => s?.player?.id === user.id) ?? -1)
        : -1;
      const enriched = { ...hand, humanSeat: humanSeat >= 0 ? humanSeat : null };
      setHandHistory(prev => ({ ...prev, [tableId]: [...(prev[tableId] ?? []), enriched] }));
    });

    // Hand history — bulk load on table subscribe
    s.on('hand:history_list', ({ tableId, hands }) => {
      const enrich = h => {
        const humanSeat = user?.id
          ? (h.seats?.findIndex(s => s?.player?.id === user.id) ?? -1)
          : -1;
        return { ...h, humanSeat: humanSeat >= 0 ? humanSeat : null };
      };
      setHandHistory(prev => ({ ...prev, [tableId]: hands.map(enrich) }));
    });

    /* The wallet is whatever the server says it is.
     *
     * Every server-side change emits this, including ones the player did not
     * ask for such as an automatic top-up between hands. These events carry no
     * deltas on purpose: the previous version applied one locally and posted it
     * back, duplicating a write the server had already made, and leaving a
     * table paid out twice as a result. */
    s.on('wallet:update', ({ wallet }) => {
      setWalletFromServer(wallet);
    });

    // Which seat is yours is derived from table state below, not tracked here.
    s.on('player:left', ({ tableId }) => {
      setMyCardsMap(prev => { const n = { ...prev }; delete n[tableId]; return n; });
    });

    // Waitlist position
    s.on('waitlist:position', ({ tableId, position }) => {
      setWaitlists(prev => ({ ...prev, [tableId]: position }));
    });

    return () => { s.disconnect(); socketRef.current = null; setSocket(null); setConnected(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Derived state ─────────────────────────────────────────────────────────

  // tables as array (for Dashboard)
  const tables = Object.values(tableMap).map(t => ({
    ...t,
    // backwards-compat: expose gameState fields at top level for TableView
    seats:     t.seats,
    config:    t.config,
    gameState: t.gameState,
  }));

  // games: { [tableId]: gameState } — mirrors old GameContext shape
  const games = {};
  for (const [id, t] of Object.entries(tableMap)) {
    if (t.gameState) games[id] = { ...t.gameState, config: { ...t.config, type: t.type }, seats: t.seats };
  }

  // sittingOut: { [tableId]: [seatIndices] }
  const sittingOut = {};
  for (const [id, t] of Object.entries(tableMap)) {
    sittingOut[id] = t.sittingOut ?? [];
  }

  // missedBlinds: { [tableId]: { [seat]: { sb, bb } } }
  const missedBlinds = {};
  for (const [id, t] of Object.entries(tableMap)) {
    missedBlinds[id] = t.missedBlinds ?? {};
  }

  // sessions: { [tableId]: seatIndex } — which seat is mine, at each table.
  //
  // Derived from the broadcast, deliberately. This used to be React state set
  // only by the one-off player:joined event, so it was empty after any page
  // reload, reconnect or server restart: the seat was still yours on the
  // server, but the client did not know which one, mySession came out
  // undefined, and every seat control disappeared at once — sit out, sit back
  // in, leave seat, post blinds, top up. Seat ownership is a fact about the
  // table state, not an event you had to be present for.
  const sessions = {};
  if (user?.id) {
    for (const [id, t] of Object.entries(tableMap)) {
      const seatIndex = (t.seats ?? []).findIndex(s => s?.player?.id === user.id);
      if (seatIndex !== -1) sessions[id] = seatIndex;
    }
  }

  // waitingForBB: { [tableId]: [seatIndices] }
  const waitingForBB = {};
  for (const [id, t] of Object.entries(tableMap)) {
    waitingForBB[id] = t.waitingForBB ?? [];
  }

  // sitOutNextBB: seats that asked to stop before paying another big blind
  const sitOutNextBB = {};
  for (const [id, t] of Object.entries(tableMap)) {
    sitOutNextBB[id] = t.sitOutNextBB ?? [];
  }

  // runItTwice: seats that agreed to run the board twice when all in
  const runItTwice = {};
  for (const [id, t] of Object.entries(tableMap)) {
    runItTwice[id] = t.runItTwice ?? [];
  }

  // autoTopUp: seats that restake from the wallet instead of sitting out
  const autoTopUp = {};
  for (const [id, t] of Object.entries(tableMap)) {
    autoTopUp[id] = t.autoTopUp ?? [];
  }

  // ── Action emitters ───────────────────────────────────────────────────────

  const emit = useCallback((event, data) => {
    socketRef.current?.emit(event, data);
  }, []);

  // Table subscription (call when entering a table page)
  const subscribeTable = useCallback((tableId) => {
    emit('table:subscribe', { tableId });
  }, [emit]);

  const unsubscribeTable = useCallback((tableId) => {
    emit('table:unsubscribe', { tableId });
  }, [emit]);

  // Seat management
  const joinSeat = useCallback((tableId, seatIndex) => {
    emit('table:join_seat', { tableId, seatIndex });
  }, [emit]);

  const leaveSeat = useCallback((tableId) => {
    emit('table:leave_seat', { tableId });
  }, [emit]);

  const moveSeat = useCallback((tableId, _fromSeat, toSeat) => {
    emit('table:move_seat', { tableId, toSeat });
  }, [emit]);

  const inviteBots = useCallback((tableId) => {
    emit('table:invite_bots', { tableId });
  }, [emit]);

  const kickBots = useCallback((tableId) => {
    emit('table:kick_bots', { tableId });
  }, [emit]);

  const joinWaitlist = useCallback((tableId) => {
    emit('table:waitlist_join', { tableId });
  }, [emit]);

  const leaveWaitlist = useCallback((tableId) => {
    emit('table:waitlist_leave', { tableId });
    setWaitlists(prev => { const n = { ...prev }; delete n[tableId]; return n; });
  }, [emit]);

  const clearDeparture = useCallback((_tableId, _userId) => {
    // Server clears on re-join; local state already removed via player:joined
  }, []);

  const topUpSeat = useCallback((_tableId, _seatIndex, _amount) => {
    // Visual stack update handled by server broadcast; actual top-up via queueTopUp
  }, []);

  const createTable = useCallback(({ name, type, stakes, config, botCount = 0 }) => {
    emit('table:create', { name, type, stakes, config, botCount });
  }, [emit]);

  // Game actions
  const beginHand = useCallback((tableId) => {
    emit('game:start_hand', { tableId });
  }, [emit]);

  const act = useCallback((tableId, action) => {
    emit('game:act', { tableId, action });
  }, [emit]);

  const stopHand = useCallback((tableId) => {
    emit('game:stop_hand', { tableId });
  }, [emit]);

  const togglePause = useCallback((tableId) => {
    emit('game:pause', { tableId });
  }, [emit]);

  // `want` is 'in' or 'out', sent explicitly. Letting the server infer it
  // from current state made one event mean three things and stranded players
  // who were waiting for the big blind.
  const toggleSitOut = useCallback((tableId, _seatIndex, want) => {
    emit('game:sit_out', { tableId, want });
  }, [emit]);

  const setSitOutNextBB = useCallback((tableId, want) => {
    emit('game:sit_out_next_bb', { tableId, want });
  }, [emit]);

  const setRunItTwice = useCallback((tableId, want) => {
    emit('game:run_it_twice', { tableId, want });
  }, [emit]);

  const setAutoTopUp = useCallback((tableId, want) => {
    emit('game:auto_top_up', { tableId, want });
  }, [emit]);

  const queueTopUp = useCallback((tableId, _seatIndex, amount) => {
    emit('game:queue_top_up', { tableId, amount });
  }, [emit]);

  const topUpStack = useCallback((tableId, seatIndex, amount) => {
    // Immediate top-up between hands — route through queueTopUp (server applies immediately if not mid-hand)
    emit('game:queue_top_up', { tableId, amount });
  }, [emit]);

  const electStraddle = useCallback((tableId, _seatIndex) => {
    emit('game:straddle', { tableId });
  }, [emit]);

  const postDeadBlinds = useCallback((tableId, _seatIndex) => {
    emit('game:post_dead_blinds', { tableId });
  }, [emit]);

  // No-ops (server handles these)
  const initGame   = useCallback(() => {}, []);
  const syncTable  = useCallback(() => {}, []);
  const recordHand = useCallback(() => {}, []);

  return (
    <SocketContext.Provider value={{
      socket, connected,
      // Tables & lobby
      tables, tableMap, sessions, waitlists, departedPlayers, DEPARTURE_WINDOW_MS,
      // Table actions
      subscribeTable, unsubscribeTable,
      joinSeat, leaveSeat, moveSeat, inviteBots, kickBots,
      joinWaitlist, leaveWaitlist, topUpSeat, clearDeparture, createTable,
      // Game state
      games, handHistory, sittingOut, waitingForBB, sitOutNextBB, runItTwice,
      autoTopUp, missedBlinds,
      myCardsMap,
      // Game actions
      initGame, syncTable, recordHand, setSitOutNextBB, setRunItTwice,
      setAutoTopUp,
      beginHand, act, stopHand, togglePause,
      topUpStack, queueTopUp, toggleSitOut, electStraddle, postDeadBlinds,
    }}>
      {children}
      {displaced && <SessionDisplaced />}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
